import React, { forwardRef } from 'react';
import {
  arrayOf,
  shape,
  string,
  bool,
} from 'prop-types';
import { extend } from '@thd-nucleus/data-sources';
import classnames from 'classnames';
import { RecommendedSearches } from './messageTypes/RecommendedSearches';
import { AssistantRecommendations } from './messageTypes/assistant-recommendations/AssistantRecommendations';
import { ExceptionMessage } from './messageTypes/ExceptionMessage';
import { MessageBubble } from './messageTypes/MessageBubble';
import ATCConfirmation from './messageTypes/assistant-recommendations/ATCConfrimation';
import { MessageFeedback } from './MessageFeedback';

const ChatMessage = forwardRef(({ message, showFeedback }, ref) => {
  const { type, content, error } = message;

  const renderMessageContent = () => {
    if (type === 'user') {
      return [<MessageBubble key="user-message" type={type}>{message.content}</MessageBubble>];
    }

    if (type === 'add-to-cart') {
      return [<ATCConfirmation itemId={message.content.itemId} />];
    }

    if (error) {
      return [<ExceptionMessage data={{}} key="error-message" />];
    }

    const { responseSummary, relatedContent, additionalDetails } = content;

    const messages = [];

    if (responseSummary?.summaryAnswer) {
      messages.push(<MessageBubble key="summary-message" type={type}>{responseSummary?.summaryAnswer}</MessageBubble>);
    }
    if (relatedContent?.recommendations?.bestMatch?.length > 0) {
      messages.push(
        <AssistantRecommendations key="bestmatch-message" products={relatedContent.recommendations?.bestMatch} />
      );
    }
    if (relatedContent?.relatedSearchResults?.length > 0) {
      messages.push(
        <RecommendedSearches key="relatedSearch-message" categories={relatedContent.relatedSearchResults} />
      );
    }
    if (additionalDetails?.inquiryDetails) {
      messages.push(<ExceptionMessage key="inqueryDetails-message" data={additionalDetails.inquiryDetails} />);
    }

    return messages;
  };

  const chatMessageClasses = classnames('sui-flex sui-flex-col sui-mb-4', {
    'sui-align-end': type === 'user',
    'sui-align-start': type === 'bot'
  });

  return (
    <div data-testid="chat-message" className={chatMessageClasses} ref={ref}>
      {renderMessageContent()}
      {showFeedback && (
        <MessageFeedback message={message} />
      )}
    </div>
  );
});

ChatMessage.propTypes = {
  showFeedback: bool,
  message: shape({
    relatedContent: shape({
      relatedSearchResults: arrayOf(shape({
        itemId: string,
        price: string,
        imageUrl: string,
        rating: string,
        description: string,
        href: string,
        category: arrayOf(string),
        searchTerm: string
      })),
      recommendations: shape({
        bestMatch: arrayOf(shape({
          itemId: string,
          price: string,
          averageRating: string,
          imageUrl: string,
          href: string,
          productName: string
        }))
      })
    }),
    suggestedQuestions: arrayOf(string),
    followUpQuestions: arrayOf(string),
    additionalDetails: shape({
      inquiryDetails: shape({
        telephoneNumber: string,
        description: string,
        referenceUrl: string
      })
    })
  }).isRequired,
};

ChatMessage.defaultProps = {
  showFeedback: false,
};

ChatMessage.dataModel = extend(
  AssistantRecommendations,
  ATCConfirmation,
  MessageFeedback,
);

export { ChatMessage };
